import { useCallback, useState } from 'react'

import { IconAlertCircle } from '@tabler/icons-react'
import { merge, uniq } from 'lodash'
import { FormattedMessage, useIntl } from 'react-intl'

import type { Budget as BudgetType } from '@subaio/api-types/budget'
import { SnackbarType, useAsyncResource, useDebouncedCallback, useSnackbar } from '@subaio/components'
import { Form } from '@subaio/components/src/HookForm'
import { cases, delay, logError, RequestError } from '@subaio/utils'

import { BrandFeatures } from 'apiTypes/BrandFeatures'

import type { ServiceType } from 'enums'

import { collectCategories } from 'lib/collectCategories'
import { removeUserAdjustments } from 'lib/removeUserAdjustments'

import type { Step, StepType } from 'containers/Flow/Step'
import { UserType } from 'containers/Flow/stepHandler'

import { ButtonSolidSubmit } from 'components/Buttons'
import { Input } from 'components/FormFields'
import { Spinner } from 'components/Spinner'

import { ButtonGroup } from '../ButtonGroup'
import { DefaultErrorSteps } from '../DefaultErrorSteps'
import { InfoBox } from '../InfoBox'
import { Wrapper } from '../Wrapper'
import { Budget } from './Budget'
import { Card } from './Card'
import { Container } from './Container'
import { Content } from './Content'
import { ErrorWrapper } from './ErrorWrapper'
import { HeaderIconWrapper } from './HeaderIconWrapper'

type Model = {
	comment?: string
	categories?: Record<string, { amount: number | undefined }>
}

type Props = TaggedType<Step, StepType.BudgetEditForm> & {
	onAddComment: (comment: string) => Promise<void>
	onAdjustCategoryAmount: (args: { categoryId: string; amount: number | undefined }) => Promise<BudgetType>
	onGetBudget: () => Promise<BudgetType | undefined>
	onRecategorizeService: (args: {
		type: ServiceType
		serviceId: string
		categoryId: string
		subCategoryId: string
	}) => Promise<BudgetType>
	onSubmitBudget: () => Promise<void>
}

export const BudgetEditForm = ({
	brandFeatures,
	userMetadata,
	onAddComment,
	onAdjustCategoryAmount,
	onGetBudget,
	onSubmitBudget,
	onRecategorizeService,
}: Props) => {
	const { showSnackbar } = useSnackbar()
	const { formatMessage } = useIntl()
	const { userType } = userMetadata

	const budgetResource = useAsyncResource(
		useCallback(
			async (abortIfCancelled) => {
				let budget: BudgetType | undefined
				// We wait for the budget to be available. Will return an empty response with status code 202 while there is no bank data
				do {
					abortIfCancelled()
					budget = await onGetBudget()
					abortIfCancelled()
					// The budget-api can get overloaded if called too quickly
					if (!budget) await delay(7)
					abortIfCancelled()
				} while (!budget)

				return budget
			},
			[onGetBudget],
		),
	)

	// Because the call to adjust a category totalMonthlyAmountUserProvided can be slow at times,
	// we want to give feedback and block interaction/submission while waiting for it to get through
	const [fieldsBeingSubmitted, setFieldsBeingSubmitted] = useState<Array<string>>([])

	const onRecategorizeUncategorizedService = useCallback(
		async ({
			type,
			categoryId,
			subCategoryId,
			serviceId,
		}: {
			type: ServiceType
			categoryId: string
			subCategoryId: string
			serviceId: string
		}): Promise<BudgetType> => {
			if (budgetResource.status !== 'done') throw new Error('Attempted to adjust a service for a non-existent budget')

			const adjustedBudget = await onRecategorizeService({
				type,
				serviceId,
				categoryId,
				subCategoryId,
			})
			await budgetResource.set(adjustedBudget)

			return adjustedBudget
		},
		[onRecategorizeService, budgetResource],
	)

	const updateBudget = useCallback(
		async (payload: { categoryId: string; amount: number | undefined }) => {
			if (budgetResource.status !== 'done') return logError('Attempted to edit a budget that had not yet loaded')

			try {
				// We rely on the backend to supply a new budget with the right values.
				// We merge the initial and new budget together to retain transactions from the initial budgets/{id}/details call.
				const adjustedBudget = await onAdjustCategoryAmount(payload)
				await budgetResource.set(merge(removeUserAdjustments(budgetResource.value), adjustedBudget))
			} catch (error) {
				// If the adjustment returns a 404, we refresh the budget to try to recover from a potential bad state.
				if (error instanceof RequestError && error.request.statusCode === 404) {
					return budgetResource.refresh()
				}
			}
		},
		[budgetResource, onAdjustCategoryAmount],
	)

	const onCategoryUpdate = useDebouncedCallback(
		async (categories: DeepPartial<Model['categories']>, resetField: (categoryId: string) => void) => {
			if (budgetResource.status !== 'done') return logError('Attempted to edit a budget that had not yet loaded')
			const resolveFieldSubmission = (categoryId: string) =>
				setFieldsBeingSubmitted((fields) => fields.filter((id) => id !== categoryId))

			// Create tuples of all newly changed fields and their initial values to make it easier to iterate over
			const changedFields: Array<[id: string, userProvidedAmount: number | undefined]> = Object.entries(
				categories ?? {},
			).map(([categoryId, value]) => [categoryId, value?.amount])
			const previousFields: Array<
				[id: string, previousUserProvidedAmount: number | null, totalMonthlyAmount: number | undefined, amount: number]
			> = Object.entries(collectCategories(budgetResource.value)).map(([categoryId, value]) => [
				categoryId,
				value.totalMonthlyAmountUserProvided,
				value.totalMonthlyAmount,
				value.amount,
			])

			// We filter away all the fields that does not warrant updating through the API
			const fieldsToUpdate = changedFields.filter(([changedId, userProvidedAmount]) =>
				previousFields.some(([initialId, previousUserProvidedAmount, totalMonthlyAmount]) => {
					if (changedId !== initialId) return false

					// If the field is unchanged and the user did not previously provide an adjustment, assume no change
					if (userProvidedAmount === totalMonthlyAmount && previousUserProvidedAmount === null) return false

					// If the field is changed but the adjustment is equal to the previously provided adjustment, assume no change
					if (userProvidedAmount !== totalMonthlyAmount && userProvidedAmount === previousUserProvidedAmount)
						return false

					// If the field gets reset but there was no adjustment made previously, assume no change
					if (userProvidedAmount === undefined && previousUserProvidedAmount === null) return false

					return true
				}),
			)

			setFieldsBeingSubmitted((fieldsBeingSubmitted) =>
				uniq([...fieldsBeingSubmitted, ...fieldsToUpdate.map(([id]) => id)]),
			)

			for (const [categoryId, userProvidedAmount] of fieldsToUpdate) {
				const previousField = previousFields.find(([previousId]) => previousId === categoryId)
				if (!previousField) return resolveFieldSubmission(categoryId)
				const [, previousUserProvidedAmount, totalMonthlyAmount] = previousField

				if (userProvidedAmount !== undefined && userProvidedAmount < 0) {
					showSnackbar({
						message: formatMessage({
							id: 'flow.budgetEditForm.invalidNegativeNumber',
							defaultMessage: 'A category cannot be a negative number',
						}),
						type: SnackbarType.Error,
					})

					return resolveFieldSubmission(categoryId)
				}

				if (
					userProvidedAmount !== undefined &&
					(Number.isNaN(userProvidedAmount) || !/^\d*$/.test(userProvidedAmount.toString()))
				) {
					showSnackbar({
						message: formatMessage({
							id: 'flow.budgetEditForm.invalidNumber',
							defaultMessage: 'A category cannot be set to anything but a whole number',
						}),
						type: SnackbarType.Error,
					})

					return resolveFieldSubmission(categoryId)
				}

				try {
					// If the userProvidedAmount is `undefined` and the previously changed userProvidedAmount is not undefined,
					// or if the provided userProvidedAmount is the same as the originally calculated userProvidedAmount,
					// we assume that the adjustment should be deleted.
					if (
						(userProvidedAmount === undefined && previousUserProvidedAmount !== null) ||
						(totalMonthlyAmount !== undefined &&
							userProvidedAmount === Math.round(totalMonthlyAmount) &&
							previousUserProvidedAmount !== null)
					) {
						await updateBudget({ categoryId, amount: undefined })
						resetField(categoryId)

						return resolveFieldSubmission(categoryId)
					}
					await updateBudget({ categoryId, amount: userProvidedAmount })
				} catch (error) {
					logError(error)
					showSnackbar({
						message: formatMessage({
							id: 'flow.budgetEditForm.submissionFailed',
							defaultMessage: 'Failed to submit your category adjustment',
						}),
						type: SnackbarType.Error,
					})
				}

				// Remove the updated field from the list once the field has been submitted
				resolveFieldSubmission(categoryId)
			}
		},
		1000,
	)

	return (
		<Wrapper>
			<Container>
				<Card>
					<h1>
						{cases(userType, {
							[UserType.Default]: (
								<FormattedMessage id='flow.budgetEditForm.title' defaultMessage='Review and adjust your budget' />
							),
							[UserType.PrimaryWithCoapplicant]: (
								<FormattedMessage
									id='flow.budgetEditForm.titleWithCoapplicant'
									defaultMessage='Review and adjust your budget'
								/>
							),
						})}
					</h1>
					<div>
						<p>
							{cases(userType, {
								[UserType.Default]: (
									<FormattedMessage
										id='flow.budgetEditForm.description'
										defaultMessage='Please review the calculated monthly amounts based on the data collected. Adapt if necessary the numbers so they reflect your current finances. If you adjust the figures, please note that these must be on a monthly basis.'
									/>
								),
								[UserType.PrimaryWithCoapplicant]: (
									<FormattedMessage
										id='flow.budgetEditForm.descriptionWithCoapplicant'
										defaultMessage='Please review the calculated monthly amounts based on the data collected. If necessary, adjust the figures to reflect your current finances. If you adjust the figures, please note that these should be on a monthly basis.'
									/>
								),
							})}
						</p>
						<p>
							{cases(userType, {
								[UserType.Default]: (
									<FormattedMessage
										id='flow.budgetEditForm.description2'
										defaultMessage='You also have the option of sending a message to your advisor along with the budget.'
									/>
								),
								[UserType.PrimaryWithCoapplicant]: (
									<FormattedMessage
										id='flow.budgetEditForm.description2WithCoapplicant'
										defaultMessage='You also have the option to send a message to your advisor along with the budget.'
									/>
								),
							})}
						</p>
						<p>
							{cases(userType, {
								[UserType.Default]: (
									<FormattedMessage
										id='flow.budgetEditForm.description3'
										defaultMessage='When you are done, click "Submit budget".'
									/>
								),
								[UserType.PrimaryWithCoapplicant]: (
									<FormattedMessage
										id='flow.budgetEditForm.description3WithCoapplicant'
										defaultMessage='When you are done, click "Submit budget".'
									/>
								),
							})}
						</p>
					</div>
					{budgetResource.status === 'loading' && !budgetResource.value && (
						<HeaderIconWrapper>
							<h3>
								<FormattedMessage
									id='flow.budgetEditForm.loading'
									defaultMessage='We are collecting your data - please wait'
								/>
							</h3>
							<Spinner />
						</HeaderIconWrapper>
					)}
					{budgetResource.status === 'loading' && budgetResource.value && (
						<HeaderIconWrapper>
							<h3>
								<FormattedMessage
									id='flow.budgetEditForm.updating'
									defaultMessage='We are updating your budget - please wait'
								/>
							</h3>
							<Spinner />
						</HeaderIconWrapper>
					)}
					{budgetResource.status === 'errored' && (
						<ErrorWrapper>
							<InfoBox type='danger'>
								<IconAlertCircle size={20} />
								<div>
									<p>
										<FormattedMessage
											id='flow.budgetEditForm.loadErrorTitle'
											defaultMessage='Failed to generate your budget'
										/>
									</p>
									<DefaultErrorSteps />
								</div>
							</InfoBox>
						</ErrorWrapper>
					)}
					{budgetResource.status === 'done' && (
						<Form<Model>
							initialValues={budgetResource.value ? { categories: collectCategories(budgetResource.value) } : undefined}
							validationMode='onChange'
							render={() => (
								<Content>
									<Budget
										{...budgetResource.value}
										// FIXME: Forced currency to DKK since the BEC market operates primarily with "kr". Should probably at least depend on brand country
										currency='DKK'
										fieldsBeingSubmitted={fieldsBeingSubmitted}
										isUncategorizedEnabled={brandFeatures.includes(BrandFeatures.Uncategorized)}
										isUncategorizedIncomeEnabled={brandFeatures.includes(BrandFeatures.UncategorizedIncome)}
										onRecategorizeUncategorizedService={onRecategorizeUncategorizedService}
									/>
									<Input
										label={cases(userType, {
											[UserType.Default]: (
												<FormattedMessage
													id='flow.budgetEditForm.labelComment'
													defaultMessage='Comment on your budget'
												/>
											),
											[UserType.PrimaryWithCoapplicant]: (
												<FormattedMessage
													id='flow.budgetEditForm.labelCommentWithCoapplicant'
													defaultMessage='Comment on your budget'
												/>
											),
										})}
										type='multiline'
										name='comment'
									/>
									<ButtonGroup>
										<ButtonSolidSubmit
											disabled={fieldsBeingSubmitted.length > 0}
											label={<FormattedMessage id='flow.budgetEditForm.buttonSubmit' defaultMessage='Submit budget' />}
										/>
									</ButtonGroup>
								</Content>
							)}
							htmlFormProps={{
								onKeyDown: (event) => {
									// We want to prevent the form from submitting when pressing the enter key in this form
									// but also still allow newlines in the comment field (or any textarea field really).
									if (
										event.key === 'Enter' &&
										event.target instanceof HTMLInputElement &&
										event.target.type !== 'textarea'
									) {
										return event.preventDefault()
									}
								},
							}}
							onChange={async (fields, { setValue }) => {
								if (!fields?.categories || 'amount' in fields.categories) return
								await onCategoryUpdate(fields.categories, (categoryId: string) => {
									setValue(
										`categories.${categoryId}.amount`,
										collectCategories(budgetResource.value)[categoryId].totalMonthlyAmount,
									)
								})
							}}
							onSubmit={async (fields) => {
								if (fields.comment) await onAddComment(fields.comment)
								await onSubmitBudget()
							}}
						/>
					)}
				</Card>
			</Container>
		</Wrapper>
	)
}
